import { useEffect, useState } from "react";
import { absUrl } from "@/lib/api";

/**
 * BossCutin — a ~2.2s full-viewport cinematic reveal for boss encounters.
 *
 * Sequence (all CSS-keyframe driven, no JS animation state machine):
 *   0.00s  Black wipe covers viewport (300ms)
 *   0.30s  Boss portrait slides in from right, name types in from center
 *   1.00s  Hold — "BOSS" pill blinks, gold underline sweeps
 *   1.60s  Red vignette pulse + subtle horizontal shake
 *   1.90s  Cross-dissolve out (350ms)
 *   2.25s  Component unmounts
 *
 * Trigger: parent renders <BossCutin key=... /> with a unique key per new boss
 * encounter (e.g., `${runId}-${enemyName}`) so remount replays the animation.
 * onDone fires when the animation finishes so parent can unmount cleanly.
 */
export default function BossCutin({ enemyName, enemyPortraitUrl, onDone }) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => {
      setVisible(false);
      if (onDone) onDone();
    }, 2250);
    return () => clearTimeout(t);
  }, [onDone]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[200] boss-cutin-root pointer-events-none"
      data-testid="boss-cutin"
      aria-hidden="true"
    >
      {/* Full-screen black wipe */}
      <div className="boss-cutin-black absolute inset-0" />

      {/* Red vignette that pulses in during the "roar" beat */}
      <div className="boss-cutin-vignette absolute inset-0" />

      {/* Centerpiece: portrait right, name left */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="boss-cutin-shake grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-10 max-w-5xl w-full px-8">
          {/* NAME BLOCK (types in from center-left) */}
          <div className="boss-cutin-name-block text-center md:text-right">
            <div className="text-xs tracking-[0.5em] blood-text mb-3 boss-cutin-pill inline-block px-3 py-1 border border-[#d96534]/70 rounded">
              ⚔ BOSS ⚔
            </div>
            <h1
              className="boss-cutin-name font-heading text-5xl md:text-7xl leading-tight"
              style={{ color: "#e6c07a" }}
            >
              {enemyName || "The Unnamed Horror"}
            </h1>
            <div className="boss-cutin-underline mt-4 mx-auto md:ml-auto md:mr-0" />
            <div className="mt-3 text-[10px] tracking-[0.4em] ash-text">
              STANDS IN YOUR PATH
            </div>
          </div>

          {/* PORTRAIT (slides in from right) */}
          <div className="boss-cutin-portrait relative w-56 h-72 md:w-72 md:h-96 overflow-hidden panel border-2"
               style={{ borderColor: "rgba(217,101,52,0.7)", boxShadow: "0 0 60px rgba(217,101,52,0.5)" }}>
            {enemyPortraitUrl ? (
              <img
                src={absUrl(enemyPortraitUrl)}
                alt={enemyName || "Boss"}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center"
                   style={{ background: "radial-gradient(ellipse at center, #3a0a05 0%, #0a0503 70%)" }}>
                <span className="text-6xl">☠</span>
              </div>
            )}
            {/* Gold-red gradient sheen sweep */}
            <div className="boss-cutin-sheen absolute inset-0 pointer-events-none" />
          </div>
        </div>
      </div>
    </div>
  );
}
